import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AudioLines,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  CloudDownload,
  Download,
  Globe2,
  KeyRound,
  Languages,
  LoaderCircle,
  LocateFixed,
  LockKeyhole,
  LogOut,
  MapPin,
  MapPinned,
  Palette,
  RefreshCcw,
  Save,
  Settings2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Trash2,
  UserRound,
  Volume2,
  X,
  Zap,
} from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { Button, Card, SectionTitle, Toast } from "../components/ui";
import { FrenchVoiceValidation } from "../components/FrenchVoiceValidation";
import { useUiFeedback } from "../feedback";
import { requestCurrentPosition, type StoredLocation } from "../location";
import {
  englishVoices,
  frenchVoices,
  inferredVoiceGender,
  selectFrenchVoice,
  selectVoice,
  speakEnglish,
  speakFrench,
  voiceQualityLabel,
} from "../speech";

type AssistantStyle = "practical" | "balanced" | "warm";
type GptTextModel = "gpt-5.6-sol" | "gpt-5.6-terra" | "gpt-5.6-luna";
type GptImageModel = "gpt-image-2";
type GptModel = GptTextModel | GptImageModel;
type AiRouting = {
  provider: "kimi" | "gptRelay";
  model: string;
  chatModel: GptTextModel;
  deepModel: GptTextModel;
  contentModel: GptTextModel;
  imageModel: GptImageModel;
};
type SettingsData = {
  profile: { displayName: string; role: "admin" | "member" };
  canManageIntegrations: boolean;
  settings: {
    dailyWordGoal?: number;
    dailyNewWords?: number;
    lessonMinutes?: number;
    voiceAccent?: "en-GB" | "en-US";
    voiceGender?: "female" | "male";
    voiceURI?: string;
    voiceRate?: number;
    voiceAutoplay?: boolean;
    frenchDailyNewWords?: number;
    frenchLessonMinutes?: number;
    frenchVoiceGender?: "female" | "male";
    frenchVoiceURI?: string;
    frenchVoiceRate?: number;
    frenchVoiceAutoplay?: boolean;
    learningAiMode?: "off" | "smart" | "always";
    learningAiAutoDailyLimit?: number;
    ieltsTarget?: number;
    delfTargetLevel?: "A1" | "A2" | "B1" | "B2";
    newsTopics?: string[];
    travelPace?: string;
    theme?: string;
    assistantStyle?: AssistantStyle;
    location?: StoredLocation;
  };
  aiRouting: AiRouting;
  integrations: {
    kimi: { configured: boolean; masked: string };
    amap: { configured: boolean; masked: string };
    gptRelay: {
      configured: boolean;
      masked: string;
      baseUrl: string;
      verifiedAt: string | null;
      supportedModels: GptModel[];
    };
    frdic: {
      configured: boolean;
      masked: string;
      verifiedAt: string | null;
      categoryCount: number;
    };
  };
};
type FrdicImportJob={id:string;status:"queued"|"running"|"completed"|"failed";stage:string;message:string;progress:{categoryIndex?:number;categoryCount?:number;page?:number;rawRows?:number};stats?:FrdicImportStatus["stats"]&{rawRows?:number};error?:string|null;updatedAt:string};
type FrdicImportStatus={integration:SettingsData["integrations"]["frdic"];stats:{total:number;words:number;phrases:number;sentences:number;matched:number;unmatched:number;ambiguous:number;inactive:number;memberships:number;categories:number;duplicateMemberships:number};latestJob:FrdicImportJob|null};
type NeuralAuditions={ready:boolean;engine:string;modelLicense?:string;generatedLocally?:boolean;items:Array<{id:string;language:"en"|"fr";voice:string;accent:"en-GB"|"en-US"|"fr-FR";gender:"female"|"male";label:string;text:string;url:string;durationMs:number}>};
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function ScenarioRouting({
  routing,
  supported,
  pending,
  onChange,
}: {
  routing: AiRouting;
  supported: GptModel[];
  pending: boolean;
  onChange: (
    value: Partial<
      Pick<AiRouting, "chatModel" | "deepModel" | "contentModel" | "imageModel">
    >,
  ) => void;
}) {
  const textModels = supported.filter(
    (model): model is GptTextModel =>
      model === "gpt-5.6-sol" ||
      model === "gpt-5.6-terra" ||
      model === "gpt-5.6-luna",
  );
  const label = (model: GptTextModel) =>
    model === "gpt-5.6-terra"
      ? "Terra · 日常均衡"
      : model === "gpt-5.6-sol"
        ? "Sol · 深度旗舰"
        : "Luna · 轻量经济";
  return (
    <div className="scenario-routing">
      <div className="scenario-route">
        <span>
          <Bot />
          <b>普通聊天 / 标准思考</b>
          <small>新对话默认 Terra；对话内可单独切换</small>
        </span>
        <select
          className="select"
          value={routing.chatModel}
          disabled={pending || !textModels.length}
          onChange={(event) =>
            onChange({ chatModel: event.target.value as GptTextModel })
          }
        >
          {textModels.map((model) => (
            <option key={model} value={model}>
              {label(model)}
            </option>
          ))}
        </select>
      </div>
      <div className="scenario-route">
        <span>
          <Sparkles />
          <b>深度思考</b>
          <small>开启深度后固定使用 Sol</small>
        </span>
        <strong
          className={
            supported.includes("gpt-5.6-sol") ? "authorized" : "unauthorized"
          }
        >
          gpt-5.6-sol
          {supported.includes("gpt-5.6-sol") ? " · 已授权" : " · 未授权"}
        </strong>
      </div>
      <div className="scenario-route">
        <span>
          <Languages />
          <b>课程与内容任务</b>
          <small>英法课程、单词、雅思、旅行、资讯和排期</small>
        </span>
        <select
          className="select"
          value={routing.contentModel}
          disabled={pending || !textModels.length}
          onChange={(event) =>
            onChange({
              contentModel: event.target.value as GptTextModel,
              deepModel: "gpt-5.6-sol",
            })
          }
        >
          {textModels.map((model) => (
            <option key={model} value={model}>
              {label(model)}
            </option>
          ))}
        </select>
      </div>
      <div className="scenario-route">
        <span>
          <Zap />
          <b>聊天生成图片</b>
          <small>每次 1 张 PNG；尺寸与质量可在聊天里选择</small>
        </span>
        <strong
          className={
            supported.includes("gpt-image-2") ? "authorized" : "unauthorized"
          }
        >
          gpt-image-2
          {supported.includes("gpt-image-2") ? " · 已授权" : " · 未授权"}
        </strong>
      </div>
    </div>
  );
}

function EngineSettings({
  data,
  loading,
  error,
  pending,
  onRetry,
  onChange,
}: {
  data?: SettingsData;
  loading: boolean;
  error: string;
  pending: boolean;
  onRetry: () => void;
  onChange: (value: {
    provider?: "kimi" | "gptRelay";
    chatModel?: GptTextModel;
    deepModel?: GptTextModel;
    contentModel?: GptTextModel;
    imageModel?: GptImageModel;
  }) => void;
}) {
  if (loading)
    return (
      <div className="engine-state">
        <LoaderCircle className="spin" />
        <b>正在读取服务器中的 AI 配置…</b>
        <small>读取完成前不会把未知状态误显示为 Kimi。</small>
      </div>
    );
  if (!data)
    return (
      <div className="engine-state error">
        <AlertCircle />
        <b>暂时无法读取 AI 引擎</b>
        <small>{error}</small>
        <Button className="secondary" onClick={onRetry}>
          <RefreshCcw />
          重新连接
        </Button>
      </div>
    );
  const relayReady = data.integrations.gptRelay.configured;
  const relayTextCount = data.integrations.gptRelay.supportedModels.filter(
    (model) => model.startsWith("gpt-5.6-"),
  ).length;
  return (
    <div className="engine-settings-content">
      <div className="routing-heading">
        <div>
          <b>当前 AI 引擎</b>
          <small>
            切换只影响之后启动的新请求和后台任务；历史内容与进行中任务不变。
          </small>
        </div>
        <span>
          {data.aiRouting.provider === "gptRelay"
            ? "GPT · 场景路由"
            : "Kimi · kimi-k3"}
        </span>
      </div>
      <div className="provider-switch">
        <button
          type="button"
          className={data.aiRouting.provider === "kimi" ? "active" : ""}
          disabled={!data.integrations.kimi.configured || pending}
          onClick={() => onChange({ provider: "kimi" })}
        >
          <Bot />
          <b>Kimi</b>
          <small>
            {data.integrations.kimi.configured
              ? "已连接，可作为备用"
              : "尚未连接"}
          </small>
        </button>
        <button
          type="button"
          className={data.aiRouting.provider === "gptRelay" ? "active" : ""}
          disabled={!relayReady || pending}
          onClick={() => onChange({ provider: "gptRelay" })}
        >
          <Sparkles />
          <b>GPT 中转</b>
          <small>
            {relayReady
              ? `${relayTextCount} 个文本模型已授权`
              : "请先在 API 设置连接"}
          </small>
        </button>
      </div>
      <ScenarioRouting
        routing={data.aiRouting}
        supported={data.integrations.gptRelay.supportedModels}
        pending={pending}
        onChange={onChange}
      />
    </div>
  );
}

function InteractionFeedbackSettings() {
  const ui = useUiFeedback();
  return (
    <Card className="feedback-settings-card">
      <SectionTitle
        title="声音与交互反馈"
        subtitle="关键学习事件有轻量提示；导航、输入和加载保持安静"
        action={<Volume2 color="var(--lav)" />}
      />
      <div className="feedback-preference-grid">
        <label className="switch-field">
          <input
            type="checkbox"
            checked={ui.preferences.uiSoundEnabled}
            onChange={(event) =>
              ui.updatePreferences({ uiSoundEnabled: event.target.checked })
            }
          />
          <span />
          <div>
            <b>关键音效</b>
            <small>提交、正误、过关和里程碑</small>
          </div>
        </label>
        <label className="switch-field">
          <input
            type="checkbox"
            checked={ui.preferences.uiMotionEnabled}
            onChange={(event) =>
              ui.updatePreferences({ uiMotionEnabled: event.target.checked })
            }
          />
          <span />
          <div>
            <b>交互动画</b>
            <small>按压、回弹、状态切换和一次性庆祝</small>
          </div>
        </label>
        <div className="field span-2">
          <label>音量 {Math.round(ui.preferences.uiSoundVolume * 100)}%</label>
          <input
            type="range"
            min="0"
            max="0.7"
            step="0.05"
            value={ui.preferences.uiSoundVolume}
            disabled={!ui.preferences.uiSoundEnabled}
            onChange={(event) =>
              ui.updatePreferences({
                uiSoundVolume: Number(event.target.value),
              })
            }
          />
        </div>
      </div>
      <div className="feedback-preview">
        <Button className="secondary" feedback="confirm">
          试听提交
        </Button>
        <Button className="secondary" feedback="correct">
          <Check />
          试听正确
        </Button>
        <Button className="secondary" feedback="complete">
          <Sparkles />
          试听过关
        </Button>
      </div>
      <p className="assistant-style-note">
        正式诊断、整套模考、听力播放与口语录音会自动进入安静模式。系统开启“减少动态效果”时，位移、摇动和粒子也会自动关闭。
      </p>
    </Card>
  );
}

export function SettingsPage() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const query = useQuery({
    queryKey: ["settings"],
    queryFn: () => api<SettingsData>("/api/settings"),
  });
  const frdicStatus=useQuery({
    queryKey:["frdic-import-status"],
    queryFn:()=>api<FrdicImportStatus>("/api/french/imports/frdic/status"),
    refetchInterval:query=>["queued","running"].includes(query.state.data?.latestJob?.status||"")?1800:false,
  });
  const neuralAuditions=useQuery({queryKey:["learning-neural-auditions"],queryFn:()=>api<NeuralAuditions>("/api/learning/audio-auditions"),staleTime:Infinity});
  const [toast, setToast] = useState("");
  const [frdicAuthorization,setFrdicAuthorization]=useState("");
  const [audition,setAudition]=useState<{url:string;label:string;text:string}|null>(null);
  const [keys, setKeys] = useState({ kimiApiKey: "", amapKey: "" });
  const [relay, setRelay] = useState({ baseUrl: "", apiKey: "" });
  const [settingsDialog, setSettingsDialog] = useState<
    "api" | "engine" | "account" | null
  >(null);
  const [learningPrefsOpen, setLearningPrefsOpen] = useState(false);
  const [account, setAccount] = useState({
    displayName: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [prefs, setPrefs] = useState<{
    dailyWordGoal: number;
    dailyNewWords: number;
    lessonMinutes: number;
    voiceAccent: "en-GB" | "en-US";
    voiceGender: "female" | "male";
    voiceURI: string;
    voiceRate: number;
    voiceAutoplay: boolean;
    frenchDailyNewWords: number;
    frenchLessonMinutes: number;
    frenchVoiceGender: "female" | "male";
    frenchVoiceURI: string;
    frenchVoiceRate: number;
    frenchVoiceAutoplay: boolean;
    learningAiMode: "off" | "smart" | "always";
    learningAiAutoDailyLimit: number;
    ieltsTarget: number;
    delfTargetLevel: "A1" | "A2" | "B1" | "B2";
    travelPace: string;
    theme: string;
    assistantStyle: AssistantStyle;
  }>({
    dailyWordGoal: 15,
    dailyNewWords: 5,
    lessonMinutes: 15,
    voiceAccent: "en-GB",
    voiceGender: "female",
    voiceURI: "",
    voiceRate: 0.86,
    voiceAutoplay: true,
    frenchDailyNewWords: 5,
    frenchLessonMinutes: 15,
    frenchVoiceGender: "female",
    frenchVoiceURI: "",
    frenchVoiceRate: 0.84,
    frenchVoiceAutoplay: true,
    learningAiMode: "smart",
    learningAiAutoDailyLimit: 1,
    ieltsTarget: 7,
    delfTargetLevel: "A2",
    travelPace: "balanced",
    theme: "blush",
    assistantStyle: "practical",
  });
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(
    null,
  );
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [frenchVoiceList, setFrenchVoiceList] = useState<
    SpeechSynthesisVoice[]
  >([]);
  useEffect(() => {
    if (query.data) {
      setPrefs((current) => ({ ...current, ...query.data.settings }));
      setRelay((current) => ({
        ...current,
        baseUrl:
          current.baseUrl || query.data.integrations.gptRelay.baseUrl || "",
      }));
      setAccount((current) => ({
        ...current,
        displayName: query.data.profile.displayName,
      }));
    }
  }, [query.data]);
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);
  useEffect(() => {
    const refreshVoices = () => {
      setVoices(englishVoices());
      setFrenchVoiceList(frenchVoices());
    };
    refreshVoices();
    speechSynthesis.addEventListener?.("voiceschanged", refreshVoices);
    return () =>
      speechSynthesis.removeEventListener?.("voiceschanged", refreshVoices);
  }, []);
  useEffect(() => {
    if (!settingsDialog) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSettingsDialog(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", close);
    };
  }, [settingsDialog]);
  const savePrefs = useMutation({
    mutationFn: () =>
      api("/api/settings", { method: "PUT", body: JSON.stringify(prefs) }),
    onSuccess: () => {
      setToast("偏好已保存");
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
  const saveKeys = useMutation({
    mutationFn: () =>
      api("/api/settings/integrations", {
        method: "PUT",
        body: JSON.stringify(keys),
      }),
    onSuccess: () => {
      setKeys({ kimiApiKey: "", amapKey: "" });
      setToast("密钥已加密保存到你的服务器");
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
  const connectRelay = useMutation({
    mutationFn: () =>
      api<{ integration: { supportedModels: GptModel[] } }>(
        "/api/settings/integrations/gpt-relay",
        {
          method: "PUT",
          body: JSON.stringify({
            baseUrl: relay.baseUrl,
            ...(relay.apiKey ? { apiKey: relay.apiKey } : {}),
          }),
        },
      ),
    onSuccess: (data) => {
      setRelay((current) => ({ ...current, apiKey: "" }));
      setToast(
        `中转站验证成功，发现 ${data.integration.supportedModels.length} 个已授权模型`,
      );
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "中转站验证失败"),
  });
  const connectFrdic=useMutation({
    mutationFn:()=>api<{integration:SettingsData["integrations"]["frdic"]}>("/api/settings/integrations/frdic",{method:"PUT",body:JSON.stringify(frdicAuthorization?{authorization:frdicAuthorization}:{})}),
    onSuccess:data=>{setFrdicAuthorization("");setToast(`法语助手已连接，识别到 ${data.integration.categoryCount} 个分类`);qc.invalidateQueries({queryKey:["settings"]});qc.invalidateQueries({queryKey:["frdic-import-status"]})},
    onError:error=>setToast(error instanceof Error?error.message:"法语助手授权验证失败"),
  });
  const syncFrdic=useMutation({
    mutationFn:()=>api<FrdicImportJob>("/api/french/imports/frdic/sync",{method:"POST",body:"{}"}),
    onSuccess:data=>{setToast(data.status==="running"?"法语生词本正在后台同步":"法语生词本已进入同步队列");qc.invalidateQueries({queryKey:["frdic-import-status"]})},
    onError:error=>setToast(error instanceof Error?error.message:"无法开始同步"),
  });
  const switchRouting = useMutation({
    mutationFn: (value: {
      provider?: "kimi" | "gptRelay";
      chatModel?: GptTextModel;
      deepModel?: GptTextModel;
      contentModel?: GptTextModel;
      imageModel?: GptImageModel;
    }) =>
      api("/api/settings/ai-routing", {
        method: "PUT",
        body: JSON.stringify(value),
      }),
    onSuccess: () => {
      setToast("AI 场景路由已保存；只影响之后启动的新请求");
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["assistant-settings"] });
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "切换失败"),
  });
  const locate = useMutation({
    mutationFn: async () => {
      const coords = await requestCurrentPosition();
      return api<{ location: StoredLocation }>("/api/settings/location", {
        method: "POST",
        body: JSON.stringify(coords),
      });
    },
    onSuccess: (data) => {
      setToast(`已将 ${data.location.label} 设为当前位置`);
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "暂时无法取得当前位置"),
  });
  const clearLocation = useMutation({
    mutationFn: () => api("/api/settings/location", { method: "DELETE" }),
    onSuccess: () => {
      setToast("已清除保存的位置");
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
  });
  const updateProfile = useMutation({
    mutationFn: () =>
      api<{ user: { displayName: string } }>("/api/auth/profile", {
        method: "PATCH",
        body: JSON.stringify({ displayName: account.displayName }),
      }),
    onSuccess: async (data) => {
      setToast("昵称已更新");
      setAccount((current) => ({
        ...current,
        displayName: data.user.displayName,
      }));
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["settings"] }),
        refresh(),
      ]);
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "昵称修改失败"),
  });
  const changePassword = useMutation({
    mutationFn: () =>
      api("/api/auth/password", {
        method: "PUT",
        body: JSON.stringify({
          currentPassword: account.currentPassword,
          newPassword: account.newPassword,
          confirmPassword: account.confirmPassword,
        }),
      }),
    onSuccess: async () => {
      setAccount((current) => ({
        ...current,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
      setToast("密码已更新，其他设备需要使用新密码重新登录");
      await refresh();
    },
    onError: (error) =>
      setToast(error instanceof Error ? error.message : "密码修改失败"),
  });
  const logout = useMutation({
    mutationFn: () => api("/api/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.clear();
      window.location.replace("/");
    },
  });
  async function install() {
    if (installPrompt) {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (choice.outcome === "accepted") setToast("已添加到桌面");
      setInstallPrompt(null);
    } else
      setToast(
        "iPhone：Safari 分享 → 添加到主屏幕；Android：浏览器菜单 → 安装应用",
      );
  }
  const selectedEnglishVoice = selectVoice(prefs);
  const selectedFrenchVoice = selectFrenchVoice(prefs);
  const englishVoiceValue =
    prefs.voiceURI && selectedEnglishVoice?.voiceURI === prefs.voiceURI
      ? prefs.voiceURI
      : "";
  const frenchVoiceValue =
    prefs.frenchVoiceURI &&
    selectedFrenchVoice?.voiceURI === prefs.frenchVoiceURI
      ? prefs.frenchVoiceURI
      : "";
  const dialogTitle =
    settingsDialog === "api"
      ? "API 设置"
      : settingsDialog === "engine"
        ? "AI 引擎设置"
        : "账户与安全";
  const dialogEyebrow =
    settingsDialog === "api"
      ? "PRIVATE CONNECTIONS"
      : settingsDialog === "engine"
        ? "MODEL ROUTING"
        : "CURRENT ACCOUNT";
  const dialogDescription =
    settingsDialog === "api"
      ? "集中管理 Kimi、高德地图和 ChatGPT 中转站，密钥只加密保存在服务器。"
      : settingsDialog === "engine"
        ? "选择当前 AI 供应商，并为聊天、深度思考、课程内容和图片生成分配模型。"
        : "只修改当前登录账号的昵称和密码；要修改其他账号，请先切换到它。";
  return (
    <div className="settings-layout" data-ai-sensitive="true">
      {toast && <Toast message={toast} />}
      <div className="settings-main">
        <Card className="settings-hub-card">
          <SectionTitle
            title="个人与位置"
            subtitle="管理当前账户的安全信息和 AI 默认位置"
            action={<Settings2 color="var(--lav)" />}
          />
          <div className="settings-entry-list">
            <button
              type="button"
              className="settings-entry account"
              onClick={() => setSettingsDialog("account")}
            >
              <span>
                <UserRound />
              </span>
              <div>
                <b>账户与安全</b>
                <small>修改昵称、重设登录密码与保护其他设备会话</small>
              </div>
              <em>
                {query.isPending
                  ? "正在读取"
                  : query.data?.profile.displayName || "私人账户"}
              </em>
              <ChevronRight />
            </button>
          </div>
          <div className="divider" />
          <div className="location-setting">
            <span>
              <MapPin />
            </span>
            <div>
              <h3>AI 默认位置</h3>
              <p>
                {query.data?.settings.location ? (
                  <>
                    当前位置：<b>{query.data.settings.location.label}</b>
                    <br />
                    AI 会用城市和区县查询天气，经纬度不会发送给 Kimi。
                  </>
                ) : (
                  "授权一次后，AI 就能理解“我这里”“明天会下雨吗”等问题。"
                )}
              </p>
            </div>
          </div>
          <div className="location-actions">
            <Button
              className="secondary"
              loading={locate.isPending}
              disabled={!query.data?.integrations.amap.configured}
              onClick={() => locate.mutate()}
            >
              <LocateFixed size={16} />
              {query.data?.settings.location ? "更新当前位置" : "使用当前位置"}
            </Button>
            {query.data?.settings.location && (
              <Button
                className="ghost"
                loading={clearLocation.isPending}
                onClick={() => clearLocation.mutate()}
              >
                <Trash2 size={15} />
                清除
              </Button>
            )}
          </div>
          <small className="location-privacy">
            定位时经纬度会发送给你已连接的高德服务做城市识别，并保存在你的私人服务器。浏览器会先征求权限。
          </small>
        </Card>
        <Card>
          <SectionTitle
            title="AI 回复风格"
            subtitle="调整表达方式；推理强度可在每个助手对话中单独选择"
            action={<Bot color="var(--lav)" />}
          />
          <div className="assistant-style-options">
            {(
              [
                {
                  value: "practical",
                  title: "务实",
                  description: "简洁、专注、直接",
                },
                {
                  value: "balanced",
                  title: "平衡",
                  description: "先给结论，再补充必要解释",
                },
                {
                  value: "warm",
                  title: "亲和",
                  description: "温暖自然，但不堆砌客套话",
                },
              ] as const
            ).map((option) => (
              <button
                type="button"
                key={option.value}
                className={
                  prefs.assistantStyle === option.value ? "active" : ""
                }
                onClick={() =>
                  setPrefs({ ...prefs, assistantStyle: option.value })
                }
              >
                <span>
                  {prefs.assistantStyle === option.value ? (
                    <Check />
                  ) : (
                    <Sparkles />
                  )}
                </span>
                <b>{option.title}</b>
                <small>{option.description}</small>
              </button>
            ))}
          </div>
          <p className="assistant-style-note">
            课程、旅行、新闻和复杂分析自动使用高强度推理；普通助手可按对话选择自动、标准或深度。
          </p>
          <Button
            onClick={() => savePrefs.mutate()}
            loading={savePrefs.isPending}
          >
            <Save size={16} />
            保存 AI 风格
          </Button>
        </Card>
        <InteractionFeedbackSettings />
        <Card>
          <SectionTitle
            title="英语语音与发音"
            subtitle="锁定所选英语口音，并优先使用设备中的高清自然音色"
            action={<AudioLines color="var(--lav)" />}
          />
          <div className="voice-summary">
            <span>
              <Languages />
            </span>
            <div>
              <b>当前实际教学声音</b>
              <p>
                {selectedEnglishVoice
                  ? `${selectedEnglishVoice.name} · ${selectedEnglishVoice.lang} · ${voiceQualityLabel(selectedEnglishVoice)}`
                  : "未找到所选口音的可用声音"}{" "}
                · {prefs.voiceRate.toFixed(2)}×
              </p>
            </div>
            <Button
              className="secondary"
              disabled={!selectedEnglishVoice}
              onClick={() => {
                if (!speakEnglish("Small steps make real progress.", prefs))
                  setToast("未检测到所选英语口音的声音");
              }}
            >
              <Volume2 />
              试听英语
            </Button>
          </div>
          {neuralAuditions.data?.ready&&<details className="neural-auditions">
            <summary><AudioLines/> 本地神经语音试听（20 条）</summary>
            <p>Kokoro 82M 在这台 Mac 本地生成，不调用 GPT/Kimi API。先试听四种候选声音，确定默认女声和备用男声后再批量制作正式课程音频。</p>
            <div className="neural-audition-grid">{[...new Set(neuralAuditions.data.items.filter(item=>item.language==="en").map(item=>item.voice))].map(voice=>{const samples=neuralAuditions.data!.items.filter(item=>item.voice===voice),first=samples[0];return <section key={voice}><div><b>{first.label}</b><small>{first.accent} · {first.gender==="female"?"女声":"男声"}</small></div>{samples.map((sample,index)=><button type="button" key={sample.id} className={audition?.url===sample.url?"active":""} onClick={()=>setAudition({url:sample.url,label:sample.label,text:sample.text})}><Volume2/>样本 {index+1}</button>)}</section>})}</div>
            {audition&&<div className="neural-audition-player"><div><b>{audition.label}</b><small>{audition.text}</small></div><audio key={audition.url} src={audition.url} controls autoPlay/></div>}
          </details>}
          <div className="form-grid voice-grid">
            <div className="field">
              <label>发音类型</label>
              <select
                className="select"
                value={prefs.voiceAccent}
                onChange={(e) =>
                  setPrefs({
                    ...prefs,
                    voiceAccent: e.target.value as "en-GB" | "en-US",
                    voiceURI: "",
                  })
                }
              >
                <option value="en-GB">英式英语</option>
                <option value="en-US">美式英语</option>
              </select>
            </div>
            <div className="field">
              <label>声音类型</label>
              <select
                className="select"
                value={prefs.voiceGender}
                onChange={(e) =>
                  setPrefs({
                    ...prefs,
                    voiceGender: e.target.value as "female" | "male",
                    voiceURI: "",
                  })
                }
              >
                <option value="female">女声</option>
                <option value="male">男声</option>
              </select>
            </div>
            <div className="field span-2">
              <label>设备可用音色</label>
              <select
                className="select"
                value={englishVoiceValue}
                onChange={(e) => {
                  const voice = voices.find(
                      (item) => item.voiceURI === e.target.value,
                    ),
                    gender = voice && inferredVoiceGender(voice);
                  setPrefs({
                    ...prefs,
                    voiceURI: e.target.value,
                    ...(gender && gender !== "unknown"
                      ? { voiceGender: gender }
                      : {}),
                  });
                }}
              >
                <option value="">自动选择最自然的匹配声音</option>
                {voices
                  .filter(
                    (voice) =>
                      voice.lang.toLowerCase().replace("_", "-") ===
                      prefs.voiceAccent.toLowerCase(),
                  )
                  .map((voice) => (
                    <option key={voice.voiceURI} value={voice.voiceURI}>
                      {voice.name} · {voiceQualityLabel(voice)} ·{" "}
                      {inferredVoiceGender(voice) === "unknown"
                        ? "设备未标注性别"
                        : inferredVoiceGender(voice) === "female"
                          ? "女声"
                          : "男声"}
                      {voice.localService ? " · 本机" : ""}
                    </option>
                  ))}
              </select>
              <small className="field-help">
                自动模式优先 Premium、Enhanced、Natural、Google 或 Microsoft
                等自然音色，并避开
                Compact、趣味和基础合成音色；不会跨英式/美式口音回退。
              </small>
            </div>
            <div className="field">
              <label>语速 {prefs.voiceRate.toFixed(2)}×</label>
              <input
                type="range"
                min="0.6"
                max="1.2"
                step="0.02"
                value={prefs.voiceRate}
                onChange={(e) =>
                  setPrefs({ ...prefs, voiceRate: Number(e.target.value) })
                }
              />
            </div>
            <label className="switch-field">
              <input
                type="checkbox"
                checked={prefs.voiceAutoplay}
                onChange={(e) =>
                  setPrefs({ ...prefs, voiceAutoplay: e.target.checked })
                }
              />
              <span />
              <div>
                <b>自动播放</b>
                <small>进入听力题和新词卡时自动朗读</small>
              </div>
            </label>
          </div>
          <p className="voice-note">
            正式本地音频存在时优先播放 Kokoro 神经语音，缺失时才使用设备 TTS。达到 B1 后只在英语听力关卡中混入英美口音及男女声。
          </p>
          <Button
            onClick={() => savePrefs.mutate()}
            loading={savePrefs.isPending}
          >
            <Save size={16} />
            保存英语语音
          </Button>
        </Card>
        <Card>
          <SectionTitle
            title="法语语音与发音"
            subtitle="每次朗读都强制使用 fr-* 法语音色，绝不调用英语声音"
            action={<Languages color="var(--rose)" />}
          />
          <div className="voice-summary">
            <span>
              <Languages />
            </span>
            <div>
              <b>没有本地词条音频时的设备回退声音</b>
              <p>
                {selectedFrenchVoice
                  ? `${selectedFrenchVoice.name} · ${selectedFrenchVoice.lang} · ${voiceQualityLabel(selectedFrenchVoice)}`
                  : "未检测到法语声音"}{" "}
                · {prefs.frenchVoiceRate.toFixed(2)}×
              </p>
            </div>
            <Button
              className="secondary"
              disabled={!selectedFrenchVoice}
              onClick={() => {
                if (!speakFrench("Bonjour, chaque petit pas compte.", prefs))
                  setToast("这台设备尚未安装法语语音；系统已阻止英语声音代替");
              }}
            >
              <Volume2 />
              试听法语
            </Button>
          </div>
          {!frenchVoiceList.length && (
            <p className="voice-warning">
              未检测到法语音色。系统不会用英语声音代替；请先在 macOS“系统设置 →
              辅助功能 → 朗读内容 → 系统声音”中下载法语高清声音。
            </p>
          )}
          <FrenchVoiceValidation canManage={Boolean(query.data?.canManageIntegrations)} onToast={setToast}/>
          {neuralAuditions.data?.ready&&neuralAuditions.data.items.some(item=>item.language==="fr")&&<details className="neural-auditions">
            <summary><AudioLines/> 标准法语本地神经语音</summary>
            <p>以下 20 条覆盖单词、连音语流和工作台真实例句，由标准法语音色 Siwis 在本机生成。当前模型没有合格的法语男声，因此不会提供虚假的男声选项，更不会调用英语音色。</p>
            <div className="neural-audition-grid french">{neuralAuditions.data.items.filter(item=>item.language==="fr").map((sample,index)=><button type="button" key={sample.id} className={audition?.url===sample.url?"active":""} onClick={()=>setAudition({url:sample.url,label:sample.label,text:sample.text})}><Volume2/>法语样本 {index+1}</button>)}</div>
            {audition&&neuralAuditions.data.items.some(item=>item.language==="fr"&&item.url===audition.url)&&<div className="neural-audition-player"><div><b>{audition.label}</b><small>{audition.text}</small></div><audio key={audition.url} src={audition.url} controls autoPlay/></div>}
          </details>}
          <div className="form-grid voice-grid">
            <div className="field">
              <label>发音语言</label>
              <select className="select" value="fr" disabled>
                <option value="fr">法语（fr-FR 优先）</option>
              </select>
            </div>
            <div className="field">
              <label>声音类型</label>
              <select
                className="select"
                value={prefs.frenchVoiceGender}
                onChange={(e) =>
                  setPrefs({
                    ...prefs,
                    frenchVoiceGender: e.target.value as "female" | "male",
                    frenchVoiceURI: "",
                  })
                }
              >
                <option value="female">女声</option>
                <option value="male">男声</option>
              </select>
            </div>
            <div className="field span-2">
              <label>设备可用法语音色</label>
              <select
                className="select"
                value={frenchVoiceValue}
                onChange={(e) => {
                  const voice = frenchVoiceList.find(
                      (item) => item.voiceURI === e.target.value,
                    ),
                    gender = voice && inferredVoiceGender(voice);
                  setPrefs({
                    ...prefs,
                    frenchVoiceURI: e.target.value,
                    ...(gender && gender !== "unknown"
                      ? { frenchVoiceGender: gender }
                      : {}),
                  });
                }}
              >
                <option value="">自动选择最自然的法语声音</option>
                {frenchVoiceList.map((voice) => (
                  <option key={voice.voiceURI} value={voice.voiceURI}>
                    {voice.name} · {voice.lang} · {voiceQualityLabel(voice)} ·{" "}
                    {inferredVoiceGender(voice) === "unknown"
                      ? "设备未标注性别"
                      : inferredVoiceGender(voice) === "female"
                        ? "女声"
                        : "男声"}
                    {voice.localService ? " · 本机" : ""}
                  </option>
                ))}
              </select>
              <small className="field-help">
                这里只接纳语言标签为 fr-* 的声音。旧设置若出现“女声 + Thomas
                男声”冲突会自动修复；课程后续关卡也不会再自动换成男声或英语声音。
              </small>
            </div>
            <div className="field">
              <label>语速 {prefs.frenchVoiceRate.toFixed(2)}×</label>
              <input
                type="range"
                min="0.6"
                max="1.2"
                step="0.02"
                value={prefs.frenchVoiceRate}
                onChange={(e) =>
                  setPrefs({
                    ...prefs,
                    frenchVoiceRate: Number(e.target.value),
                  })
                }
              />
            </div>
            <label className="switch-field">
              <input
                type="checkbox"
                checked={prefs.frenchVoiceAutoplay}
                onChange={(e) =>
                  setPrefs({ ...prefs, frenchVoiceAutoplay: e.target.checked })
                }
              />
              <span />
              <div>
                <b>自动播放</b>
                <small>进入法语听力题和新词卡时自动朗读</small>
              </div>
            </label>
          </div>
          <p className="voice-note">
            单词卡、智能训练、句子和短文优先播放已验收的本地音频；只有资源缺失或播放失败时才回退到上方设备法语声音。
          </p>
          <Button
            onClick={() => savePrefs.mutate()}
            loading={savePrefs.isPending}
          >
            <Save size={16} />
            保存法语语音
          </Button>
        </Card>
        <Card className="frdic-settings-card">
          <SectionTitle
            title="法语助手生词本"
            subtitle="官方只读 API · 分类、单词与个人语境增量同步"
            action={<CloudDownload color="var(--rose)" />}
          />
          <div className="frdic-connection-summary">
            <span className={query.data?.integrations.frdic.configured?"connected":""}>
              {query.data?.integrations.frdic.configured?<Check/>:<KeyRound/>}
            </span>
            <div>
              <b>{query.data?.integrations.frdic.configured?"已连接你的法语助手账户":"尚未连接开放 API 授权"}</b>
              <p>{query.data?.integrations.frdic.configured?`${query.data.integrations.frdic.masked} · 已识别 ${query.data.integrations.frdic.categoryCount} 个分类`:"授权只用于读取你的法语生词本，不会新增、修改或删除法语助手中的内容。"}</p>
            </div>
            {query.data?.integrations.frdic.verifiedAt&&<small>{new Date(query.data.integrations.frdic.verifiedAt).toLocaleString("zh-CN")}</small>}
          </div>
          <div className="frdic-connect-row">
            <div className="field">
              <label>法语助手开放 API 授权</label>
              <div className="input-icon">
                <KeyRound />
                <input type="password" autoComplete="off" value={frdicAuthorization} onChange={event=>setFrdicAuthorization(event.target.value)} placeholder={query.data?.integrations.frdic.configured?"留空可重新验证现有授权":"粘贴 NIS 授权内容"}/>
              </div>
            </div>
            <a className="button secondary" href="https://my.frdic.com/OpenAPI/Authorization" target="_blank" rel="noreferrer noopener">打开授权页面</a>
            <Button loading={connectFrdic.isPending} disabled={!frdicAuthorization&&!query.data?.integrations.frdic.configured} onClick={()=>connectFrdic.mutate()}>{query.data?.integrations.frdic.configured?"重新验证":"验证并加密保存"}</Button>
          </div>
          {query.data?.integrations.frdic.configured&&<div className="frdic-sync-panel">
            <div className="frdic-sync-stats">
              <span><b>{frdicStatus.data?.stats.total||0}</b><small>去重内容</small></span>
              <span><b>{frdicStatus.data?.stats.words||0}</b><small>单词</small></span>
              <span><b>{(frdicStatus.data?.stats.phrases||0)+(frdicStatus.data?.stats.sentences||0)}</b><small>短语与整句</small></span>
              <span><b>{frdicStatus.data?.stats.matched||0}</b><small>匹配正式词条</small></span>
            </div>
            {frdicStatus.data?.latestJob&&<div className={`frdic-job-state ${frdicStatus.data.latestJob.status}`}>
              <span className={["queued","running"].includes(frdicStatus.data.latestJob.status)?"pulse-dot":""}/>
              <div><b>{frdicStatus.data.latestJob.message}</b><small>{frdicStatus.data.latestJob.status==="running"?`分类 ${frdicStatus.data.latestJob.progress.categoryIndex||0} / ${frdicStatus.data.latestJob.progress.categoryCount||0} · 已读取 ${frdicStatus.data.latestJob.progress.rawRows||0} 行`:frdicStatus.data.latestJob.error||`上次更新 ${new Date(frdicStatus.data.latestJob.updatedAt).toLocaleString("zh-CN")}`}</small></div>
            </div>}
            <div className="frdic-sync-actions">
              <Button loading={syncFrdic.isPending||["queued","running"].includes(frdicStatus.data?.latestJob?.status||"")} disabled={["queued","running"].includes(frdicStatus.data?.latestJob?.status||"")} onClick={()=>syncFrdic.mutate()}><RefreshCcw/>一键同步法语生词本</Button>
              <a className="button secondary" href="/french">打开法语个人词库</a>
            </div>
          </div>}
          <p className="voice-note">星级不会导入；重复内容只保存一份并保留全部所属分类。私人释义仅供你查看，不会覆盖 Prive 已审核的正式课程内容。</p>
        </Card>
        <Card
          className={`collapsible-settings-card ${learningPrefsOpen ? "open" : ""}`}
        >
          <button
            type="button"
            className="collapsible-settings-trigger"
            aria-expanded={learningPrefsOpen}
            onClick={() => setLearningPrefsOpen((value) => !value)}
          >
            <span>
              <Palette />
            </span>
            <div>
              <b>学习与生活偏好</b>
              <small>每日新词、课程时长、动态内容、考试目标与旅行节奏</small>
            </div>
            <em>
              {prefs.dailyNewWords} 个英语新词 · {prefs.frenchDailyNewWords}{" "}
              个法语新词
            </em>
            <ChevronDown />
          </button>
          {learningPrefsOpen && (
            <div className="collapsible-settings-content">
              <div className="form-grid">
                <div className="field">
                  <label>英语每日新词</label>
                  <select
                    className="select"
                    value={prefs.dailyNewWords}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        dailyNewWords: Number(e.target.value),
                      })
                    }
                  >
                    {[3, 5, 8, 10, 15, 20].map((value) => (
                      <option key={value} value={value}>
                        {value} 词{value === 5 ? " · 推荐" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>英语课程时长</label>
                  <select
                    className="select"
                    value={prefs.lessonMinutes}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        lessonMinutes: Number(e.target.value),
                      })
                    }
                  >
                    <option value="10">约 10 分钟</option>
                    <option value="15">约 15 分钟 · 推荐</option>
                    <option value="20">约 20 分钟</option>
                    <option value="30">约 30 分钟</option>
                  </select>
                </div>
                <div className="field">
                  <label>法语每日新词</label>
                  <select
                    className="select"
                    value={prefs.frenchDailyNewWords}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        frenchDailyNewWords: Number(e.target.value),
                      })
                    }
                  >
                    {[3, 5, 8, 10, 15, 20].map((value) => (
                      <option key={value} value={value}>
                        {value} 词{value === 5 ? " · 推荐" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>法语课程时长</label>
                  <select
                    className="select"
                    value={prefs.frenchLessonMinutes}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        frenchLessonMinutes: Number(e.target.value),
                      })
                    }
                  >
                    <option value="10">约 10 分钟</option>
                    <option value="15">约 15 分钟 · 推荐</option>
                    <option value="20">约 20 分钟</option>
                    <option value="30">约 30 分钟</option>
                  </select>
                </div>
                <div className="field">
                  <label>动态课程内容</label>
                  <select
                    className="select"
                    value={prefs.learningAiMode}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        learningAiMode: e.target.value as
                          | "off"
                          | "smart"
                          | "always",
                      })
                    }
                  >
                    <option value="smart">按适配度智能选择 · 推荐</option>
                    <option value="off">仅使用已保存内容</option>
                    <option value="always">允许更积极生成新内容</option>
                  </select>
                </div>
                <div className="field">
                  <label>每种语言每日自动调用上限</label>
                  <select
                    className="select"
                    value={prefs.learningAiAutoDailyLimit}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        learningAiAutoDailyLimit: Number(e.target.value),
                      })
                    }
                  >
                    {[0, 1, 2, 3].map((value) => (
                      <option key={value} value={value}>
                        {value === 0 ? "不自动调用" : `${value} 次`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>雅思目标分数</label>
                  <select
                    className="select"
                    value={prefs.ieltsTarget}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        ieltsTarget: Number(e.target.value),
                      })
                    }
                  >
                    {[5.5, 6, 6.5, 7, 7.5, 8].map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>DELF 目标等级</label>
                  <select
                    className="select"
                    value={prefs.delfTargetLevel}
                    onChange={(e) =>
                      setPrefs({
                        ...prefs,
                        delfTargetLevel: e.target.value as
                          | "A1"
                          | "A2"
                          | "B1"
                          | "B2",
                      })
                    }
                  >
                    {["A1", "A2", "B1", "B2"].map((v) => (
                      <option key={v} value={v}>
                        {v}
                        {v === "A2" ? " · 推荐起点" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>默认旅行节奏</label>
                  <select
                    className="select"
                    value={prefs.travelPace}
                    onChange={(e) =>
                      setPrefs({ ...prefs, travelPace: e.target.value })
                    }
                  >
                    <option value="relaxed">松弛慢游</option>
                    <option value="balanced">体验平衡</option>
                    <option value="intensive">高效打卡</option>
                  </select>
                </div>
              </div>
              <p className="voice-note">
                英语词汇、法语词汇、雅思和 DELF
                都按当前等级、薄弱项、错因与重复记录选择内容，不固定偏向本地或
                AI。已有合适内容直接使用；需要新内容时由当前模型生成、校验并永久保存，之后可复用。
              </p>
              <Button
                onClick={() => savePrefs.mutate()}
                loading={savePrefs.isPending}
              >
                <Save size={16} />
                保存偏好
              </Button>
            </div>
          )}
        </Card>
      </div>
      <aside className="settings-side">
        <Card className="install-card">
          <div className="phone-art">
            <Smartphone />
            <span>P</span>
          </div>
          <h3>安装到手机桌面</h3>
          <p>像普通 App 一样全屏打开，支持离线查看最近内容，并自动获得更新。</p>
          <Button className="full" onClick={install}>
            <Download />
            安装 Prive
          </Button>
          <small>iPhone 推荐使用 Safari 安装</small>
        </Card>
        <Card>
          <SectionTitle title="数据与隐私" />
          <div className="privacy-list">
            <span>
              <ShieldCheck />
              <b>私人服务器</b>
              <small>数据不存放在第三方工作台平台</small>
            </span>
            <span>
              <KeyRound />
              <b>密钥加密</b>
              <small>AES-256-GCM 加密后写入数据库</small>
            </span>
            <span>
              <CloudDownload />
              <b>可随时导出</b>
              <small>任务、行程、学习数据归你所有</small>
            </span>
          </div>
          <a className="button secondary full" href="/api/export" download>
            <CloudDownload size={16} />
            导出全部数据
          </a>
        </Card>
        <Button
          className="secondary full logout"
          loading={logout.isPending}
          onClick={() => logout.mutate()}
        >
          <LogOut size={16} />
          退出工作台
        </Button>
        <p className="version">
          <Sparkles /> Prive 1.0 · Built for one gentle life
        </p>
      </aside>
      {settingsDialog && (
        <div
          className="settings-dialog-backdrop"
          onPointerDown={() => setSettingsDialog(null)}
        >
          <section
            className={`settings-dialog ${settingsDialog}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-dialog-title"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <span>{dialogEyebrow}</span>
                <h2 id="settings-dialog-title">{dialogTitle}</h2>
                <p>{dialogDescription}</p>
              </div>
              <button
                type="button"
                aria-label={`关闭${dialogTitle}`}
                onClick={() => setSettingsDialog(null)}
              >
                <X />
              </button>
            </header>
            <div className="settings-dialog-body">
              {settingsDialog === "api" ? (
                <div className="api-settings-grid">
                  <section className="settings-modal-section">
                    <div className="integration">
                      <span className="integration-icon kimi">
                        <Bot />
                      </span>
                      <div>
                        <h3>Kimi K3</h3>
                        <p>保留现有稳定 AI 链路</p>
                        <small
                          className={
                            query.data?.integrations.kimi.configured
                              ? "connected"
                              : ""
                          }
                        >
                          {query.data?.integrations.kimi.configured ? (
                            <>
                              <Check />
                              已连接 {query.data.integrations.kimi.masked}
                            </>
                          ) : (
                            "尚未配置"
                          )}
                        </small>
                      </div>
                    </div>
                    <div className="field">
                      <label>Kimi API Key</label>
                      <div className="input-icon">
                        <KeyRound />
                        <input
                          type="password"
                          value={keys.kimiApiKey}
                          onChange={(e) =>
                            setKeys({ ...keys, kimiApiKey: e.target.value })
                          }
                          placeholder={
                            query.data?.integrations.kimi.configured
                              ? "输入新 Key 可替换现有配置"
                              : "sk-..."
                          }
                        />
                      </div>
                    </div>
                  </section>
                  <section className="settings-modal-section">
                    <div className="integration">
                      <span className="integration-icon amap">
                        <MapPinned />
                      </span>
                      <div>
                        <h3>高德地图 Web 服务</h3>
                        <p>地点、地址、定位与天气预报</p>
                        <small
                          className={
                            query.data?.integrations.amap.configured
                              ? "connected"
                              : ""
                          }
                        >
                          {query.data?.integrations.amap.configured ? (
                            <>
                              <Check />
                              已连接 {query.data.integrations.amap.masked}
                            </>
                          ) : (
                            "尚未配置"
                          )}
                        </small>
                      </div>
                    </div>
                    <div className="field">
                      <label>高德 Web 服务 Key</label>
                      <div className="input-icon">
                        <KeyRound />
                        <input
                          type="password"
                          value={keys.amapKey}
                          onChange={(e) =>
                            setKeys({ ...keys, amapKey: e.target.value })
                          }
                          placeholder="输入 Web 服务 API Key"
                        />
                      </div>
                    </div>
                  </section>
                  <div className="api-shared-actions">
                    <Button
                      loading={saveKeys.isPending}
                      disabled={!keys.kimiApiKey && !keys.amapKey}
                      onClick={() => saveKeys.mutate()}
                    >
                      <Save size={16} />
                      加密保存 Kimi / 高德连接
                    </Button>
                  </div>
                  <section className="settings-modal-section relay">
                    <SectionTitle
                      title="ChatGPT 中转站"
                      subtitle="OpenAI-compatible API 与 GPT Image 2 图片生成"
                      action={<Globe2 color="var(--lav)" />}
                    />
                    <div className="integration">
                      <span className="integration-icon relay">
                        <Zap />
                      </span>
                      <div>
                        <h3>GPT 中转服务</h3>
                        <p>支持 Responses API、联网搜索和图片生成</p>
                        <small
                          className={
                            query.data?.integrations.gptRelay.configured
                              ? "connected"
                              : ""
                          }
                        >
                          {query.data?.integrations.gptRelay.configured ? (
                            <>
                              <Check />
                              已验证 {query.data.integrations.gptRelay.masked}
                              {query.data.integrations.gptRelay.verifiedAt
                                ? ` · ${new Date(query.data.integrations.gptRelay.verifiedAt).toLocaleString("zh-CN")}`
                                : ""}
                            </>
                          ) : (
                            "尚未连接"
                          )}
                        </small>
                      </div>
                    </div>
                    <div className="form-grid">
                      <div className="field span-2">
                        <label>Base URL（必须包含 /v1）</label>
                        <div className="input-icon">
                          <Globe2 />
                          <input
                            type="url"
                            value={relay.baseUrl}
                            onChange={(e) =>
                              setRelay({ ...relay, baseUrl: e.target.value })
                            }
                            placeholder="https://relay.example/v1"
                          />
                        </div>
                      </div>
                      <div className="field span-2">
                        <label>GPT 中转 Key</label>
                        <div className="input-icon">
                          <KeyRound />
                          <input
                            type="password"
                            value={relay.apiKey}
                            onChange={(e) =>
                              setRelay({ ...relay, apiKey: e.target.value })
                            }
                            placeholder={
                              query.data?.integrations.gptRelay.configured
                                ? "留空复测现有 Key，输入可替换"
                                : "sk-..."
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="relay-warning">
                      <ShieldCheck />
                      <span>
                        <b>第三方服务提醒</b>
                        <small>
                          启用 GPT 后，请求内容会经过你填写的中转服务。Key
                          仅加密保存在服务器；验证失败不会覆盖旧配置。
                        </small>
                      </span>
                    </div>
                    <Button
                      loading={connectRelay.isPending}
                      disabled={
                        !relay.baseUrl ||
                        (!relay.apiKey &&
                          !query.data?.integrations.gptRelay.configured)
                      }
                      onClick={() => connectRelay.mutate()}
                    >
                      <Check size={16} />
                      {query.data?.integrations.gptRelay.configured
                        ? "重新测试并保存"
                        : "测试连接并保存"}
                    </Button>
                  </section>
                </div>
              ) : settingsDialog === "engine" ? (
                <EngineSettings
                  data={query.data}
                  loading={query.isPending}
                  error={
                    query.error instanceof Error
                      ? query.error.message
                      : "请确认本地服务已启动"
                  }
                  pending={switchRouting.isPending}
                  onRetry={() => query.refetch()}
                  onChange={(value) => switchRouting.mutate(value)}
                />
              ) : (
                <div className="account-settings-content">
                  <div className="account-current-note">
                    <ShieldCheck />
                    <span>
                      <b>
                        当前正在修改：
                        {query.data?.profile.displayName || "当前账号"}
                      </b>
                      <small>
                        此处不会跨账号修改资料。请从侧边栏底部先切换到目标账号；管理员还可在管理后台重置成员密码。
                      </small>
                    </span>
                  </div>
                  <section className="settings-modal-section">
                    <SectionTitle
                      title="修改昵称"
                      subtitle="这个名字会显示在侧边栏和私人空间中"
                      action={<UserRound color="var(--lav)" />}
                    />
                    <div className="field">
                      <label>工作台昵称</label>
                      <div className="input-icon">
                        <UserRound />
                        <input
                          value={account.displayName}
                          maxLength={30}
                          autoComplete="name"
                          onChange={(event) =>
                            setAccount({
                              ...account,
                              displayName: event.target.value,
                            })
                          }
                          placeholder="输入昵称"
                        />
                      </div>
                    </div>
                    <Button
                      loading={updateProfile.isPending}
                      disabled={!account.displayName.trim()}
                      onClick={() => updateProfile.mutate()}
                    >
                      <Save />
                      保存昵称
                    </Button>
                  </section>
                  <section className="settings-modal-section">
                    <SectionTitle
                      title="修改登录密码"
                      subtitle="至少 6 位；修改后其他设备需要重新登录"
                      action={<LockKeyhole color="var(--rose)" />}
                    />
                    <div className="account-password-fields">
                      <div className="field">
                        <label>当前密码</label>
                        <div className="input-icon">
                          <LockKeyhole />
                          <input
                            type="password"
                            value={account.currentPassword}
                            autoComplete="current-password"
                            onChange={(event) =>
                              setAccount({
                                ...account,
                                currentPassword: event.target.value,
                              })
                            }
                            placeholder="输入当前密码"
                          />
                        </div>
                      </div>
                      <div className="field">
                        <label>新密码</label>
                        <div className="input-icon">
                          <KeyRound />
                          <input
                            type="password"
                            minLength={6}
                            value={account.newPassword}
                            autoComplete="new-password"
                            onChange={(event) =>
                              setAccount({
                                ...account,
                                newPassword: event.target.value,
                              })
                            }
                            placeholder="至少 6 位"
                          />
                        </div>
                      </div>
                      <div className="field">
                        <label>再次输入新密码</label>
                        <div className="input-icon">
                          <KeyRound />
                          <input
                            type="password"
                            minLength={6}
                            value={account.confirmPassword}
                            autoComplete="new-password"
                            onChange={(event) =>
                              setAccount({
                                ...account,
                                confirmPassword: event.target.value,
                              })
                            }
                            placeholder="确认新密码"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="account-security-note">
                      <ShieldCheck />
                      <span>
                        <b>安全提醒</b>
                        <small>
                          服务器先验证当前密码，再用 bcrypt
                          重新加密。成功后会注销其他设备上的旧会话，当前设备保持登录。
                        </small>
                      </span>
                    </div>
                    <Button
                      loading={changePassword.isPending}
                      disabled={
                        !account.currentPassword ||
                        account.newPassword.length < 6 ||
                        !account.confirmPassword
                      }
                      onClick={() => changePassword.mutate()}
                    >
                      <LockKeyhole />
                      更新密码
                    </Button>
                  </section>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
